<script setup>
import { computed, ref } from 'vue';
import BarAction from '@/components/widgets/barActions/BarAction.vue';
import HeaderDialog from './HeaderDialog.vue';
import RetryFailedRequest from '@/components/widgets/RetryFailedRequest.vue';
import { useDisplay } from 'vuetify';

const { mdAndDown } = useDisplay();
const emit = defineEmits([
    'close',
    'save',
    'update:modelValue',
    'edit',
    'badgeClick',
    'new',
    'remove',
    'action',
    'retry',
]);

const props = defineProps([
    'modelValue',
    'page',
    'title',
    'titleBadges',
    'saving',
    'mode', // view, edit
    'actions',
    'rightActions',
    'hideIcons',
    'width',
    'showToggleSidebarButton',
    'onlyView',
    'saveButtonText',
    'hideNewAction',
    'hideRemoveAction',
    'hideEditAction',
    'hideSaveButton',
    'hasAttachments',
    'compactHeaderMargin',
    'errorMessage',
    'manualClose',
    'hasChanges'
]);

const show = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const showSidebar = ref(true);

const handleAction = (action) => {
    if (action === 'toggleSidebar') {
        showSidebar.value = !showSidebar.value;
        return;
    }

    emit('action', action);
};

const handleBadgeClick = (options) => {
    emit('badgeClick', options);
};
</script>

<template>
    <v-dialog
        v-model="show"
        :fullscreen="width ? undefined : true"
        :width="width || undefined"
        :scrim="true"
        transition="dialog-bottom-transition"
        class="creation-dialog"
    >
        <v-card>
            <div
                class="header-dialog-wrapper"
                :class="{ 'pb-0': compactHeaderMargin }"
            >
                <HeaderDialog
                    :page="page"
                    :isMobile="mdAndDown"
                    :title="title"
                    :titleBadges="titleBadges"
                    :hideIcons="onlyView || hideIcons"
                    :saving="saving"
                    :show="show"
                    @new="emit('new')"
                    @edit="emit('edit')"
                    @save="emit('save')"
                    @remove="emit('remove')"
                    @badge-click="handleBadgeClick"
                    @close="
                        () => {
                            if (!props.manualClose) {
                                show = false;
                            }
                            emit('close');
                        }
                    "
                    :mode="mode"
                    :save-button-text="saveButtonText"
                    :hide-save-button="hideSaveButton"
                    :hide-new-action="hideNewAction"
                    :hide-remove-action="hideRemoveAction"
                    :hide-edit-action="hideEditAction"
                    :has-changes="hasChanges"
                />
            </div>

            <div
                class="dialog-wrapper"
                :class="{ 'pt-0': compactHeaderMargin }"
            >
                <RetryFailedRequest
                    v-if="errorMessage"
                    :message="errorMessage"
                    @retry="emit('retry')"
                ></RetryFailedRequest>

                <BarAction
                    v-if="actions?.length > 0 && !onlyView"
                    :actions="actions"
                    :right-actions="rightActions"
                    :show-toggle-sidebar-button="showToggleSidebarButton"
                    :hide-remove-action="hideRemoveAction"
                    @action="handleAction"
                ></BarAction>

                <slot name="summary"></slot>

                <div>
                    <v-row v-if="!hasAttachments" class="mt-0">
                        <v-col cols="12" class="dialog-body">
                            <slot name="form"></slot>
                        </v-col>
                    </v-row>
                    <v-row
                        v-else
                        class="mt-0 overflow-hidden align-start"
                        :class="{
                            'table-expanded': !showSidebar,
                            'flex-nowrap': !mdAndDown
                        }"
                    >
                        <v-col
                            :cols="mdAndDown ? 12 : 9"
                            class="dialog-body column-table-container"
                        >
                            <slot name="form"></slot>
                        </v-col>
                        <v-col
                            :cols="mdAndDown ? 12 : 3"
                            class="dialog-body column-details-container"
                        >
                            <slot name="attachments"></slot>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
@media only screen and (min-device-width: 320px) and (max-device-width: 800px) {
    .dialog-wrapper {
        padding: 1.8rem 1.3rem;
        height: 100%;
        margin-top: 178px;
    }
    .titleModal {
        margin-left: 45px;
        flex-basis: auto;
    }
    .dialog-body {
        max-height: 100%;
    }
    .creation-dialog {
        width: calc(100% - 8px);
        height: calc(100% - 8px);
    }
}

@media only screen and (min-width: 1280px) {
    .dialog-wrapper {
        overflow: hidden;
    }
}

@media only screen and (min-width: 800px) {
    .header-dialog-wrapper {
        padding: 1.8rem 5.3rem 0 5.3rem;
    }

    .dialog-wrapper {
        padding: 0 5.3rem 1.8rem 5.3rem;
        height: 100%;
    }
    .dialog-body {
        max-height: calc(100vh - 220px);
        overflow: auto;
    }
    .creation-dialog {
        top: 48px;
        width: calc(100% - 80px);
    }
}
.dialog-bottom-transition-enter-active,
.dialog-bottom-transition-leave-active {
    transition: transform 0.2s ease-in-out;
}
.actions {
    border-top: 1px solid #e7e9eb;
    border-bottom: 1px solid #e7e9eb;
}

.no-link:hover {
    text-decoration: none;
}

:deep(.column-table-container),
:deep(.column-details-container) {
    transition: flex-basis 500ms ease-in-out;
}
:deep(.column-table-container) {
    max-width: 100%;
    overflow-x: auto;
}
:deep(.table-expanded > .column-table-container) {
    flex-basis: 100%;
}
:deep(.column-details-container) {
    height: calc(100vh - 269px);
    transition: flex-basis 1s;
    overflow-y: scroll;
}
:deep(.table-expanded > .column-details-container) {
    flex-basis: 0px;
    width: 0px;
}
</style>
